import { formatRupeeLabel } from '../../../display-values/lumpsumPlanAmount'
import { useStopSipAction } from '../../../execution/sip/stop/usePlanStopSipAction'
import { openRecommendationFundDetail } from '../../../lumpsum/plan-bottom-sheet/open/planRecommendationOpen'
import { PlanRecommendationFundTile } from '../../../plans-ui-components/PlanRecommendationFundTile'
import { SipRecommendationActionType } from '../../api/common.types'
import type { SipStopRecommendation } from '../../api/sipPlan.types'
import type { SipPlanRecommendationDetails } from '../api/sipPlanRecommendation.types'
import { SipPlanRecommendationSheetErrorState } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetErrorState'
import { SipPlanRecommendationSheetLayout } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetLayout'
import { SipPlanRecommendationSheetSkeleton } from '../sip-plan-bottom-sheet-ui-components/sheet/SipPlanRecommendationSheetSkeleton'
import { SipStopPlanRecommendationBody } from './body/SipStopPlanRecommendationBody'
import { PlansFundRoleBySipAction, PlansScreen } from '../../../analytics/plansEvents'
import {
  trackPlansSipFundTileOpen,
  trackPlansSipFundTilePrimaryCta
} from '../../../analytics/plansTrack'
import { ALLOCATION_PLANS_THEME } from '../../../lumpsum/plans-list/header/common/riskProfileHeaderTheme'

type SipStopPlanRecommendationSheetProps = {
  onDismiss: () => void
  planDetails: SipPlanRecommendationDetails | null
  isLoading: boolean
  isFetchFailed: boolean
  onRetry: () => void
  recommendation: SipStopRecommendation
  manageSipCta?: unknown
}

export function SipStopPlanRecommendationSheet({
  onDismiss,
  planDetails,
  isLoading,
  isFetchFailed,
  onRetry,
  recommendation,
  manageSipCta
}: SipStopPlanRecommendationSheetProps) {
  const currentSip = planDetails?.currentSip ?? recommendation?.currentSipAmount ?? 0
  const currentLabel = `${formatRupeeLabel({ rawAmount: currentSip })}/mo`
  const isin = recommendation?.isin ?? planDetails?.isin ?? ''
  const { onStopSip } = useStopSipAction({ isRecommendationSheetSource: true })
  const screenName = PlansScreen.SipYourPlanRecommendation
  const stopSipActionLabel = `Stop SIP of ${currentLabel}`

  return (
    <SipPlanRecommendationSheetLayout
      onDismiss={onDismiss}
      isLoading={isLoading}
      isFetchFailed={isFetchFailed}
      analyticsScreen={PlansScreen.SipYourPlanRecommendation}
    >
      {isLoading ? (
        <SipPlanRecommendationSheetSkeleton />
      ) : isFetchFailed ? (
        <SipPlanRecommendationSheetErrorState onRetry={onRetry} analyticsScreen={PlansScreen.SipYourPlanRecommendation} />
      ) : (
        <>
          <div className="px-5">
            <div className="flex flex-col gap-2">
              <div className="text-[14px] font-[500] leading-[130%]" style={{ color: ALLOCATION_PLANS_THEME.headerTextColor }}>
                Allocation Recommendation
              </div>
              <div className="text-[20px] leading-[130%] font-[400] text-[#6A6E6E] max-w-[90%]">
                {'Stop your SIP of '}
                <span className="font-[500] text-[#232323]">{currentLabel}</span>
                {' in this fund'}
              </div>
            </div>
          </div>
          <div className="px-5 pt-9 pb-4">
            <PlanRecommendationFundTile
              mfLogoUrl={planDetails?.mfLogoUrl ?? ''}
              fundName={planDetails?.fundName ?? ''}
              fundNature={planDetails?.fundNature ?? ''}
              fundCategory={planDetails?.fundCategory ?? ''}
              powerRating={planDetails?.powerRating ?? ''}
              powerRank={planDetails?.powerRank ?? null}
              purchaseMode={planDetails?.purchaseMode ?? ''}
              fundIsin={isin}
              onCardClick={() => {
                trackPlansSipFundTileOpen({
                  screenName,
                  fundRole: PlansFundRoleBySipAction[SipRecommendationActionType.STOP],
                  fundName: planDetails?.fundName ?? '',
                  fundIsin: isin
                })
                openRecommendationFundDetail(isin)
              }}
              actionFooter={{
                actionLabel: stopSipActionLabel,
                onActionClick: () => {
                  trackPlansSipFundTilePrimaryCta({
                    screenName,
                    buttonLabel: stopSipActionLabel,
                    fundName: planDetails?.fundName ?? '',
                    fundIsin: isin
                  })
                  onStopSip(recommendation, manageSipCta)
                }
              }}
            />
          </div>
          <SipStopPlanRecommendationBody planDetails={planDetails} />
        </>
      )}
    </SipPlanRecommendationSheetLayout>
  )
}
